import { notify } from '@billy/design-system'
import noop from 'lodash/noop'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { FrenchLegalStatus } from '@features/invoicingSettings/enums/frenchLegalStatus'
import { useVatConfirmationWarningModal } from '@features/invoicingSettings/hooks/useVatConfirmationWarningModal'
import { useZervantSettings } from '@features/invoicingSettings/hooks/useZervantSettings'
import { parseLegalStatus } from '@features/invoicingSettings/utils/parseLegalStatus'
import { useMarkAccountStepDone } from '@features/quickStart/hooks/useMarkAccountStepDone'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useCurrentUser } from '@modules-deprecated/app/user'

import { useDirtyRoute } from '../../../../../../../contexts/dirtyRouteContext'
import { NotificationKeys } from '../../../../../../../enums/notificationKeys'
import { useForm, useSegment } from '../../../../../../../hooks'
import { useOrganizationUiSetup } from '../../../../../../../hooks/useOrganizationUiSetup'
import { isCompanyNameDefault } from '../../../../../../../utils'
import { useUpdateOrganizationAndZervantSettings } from '../hooks/useUpdateOrganizationAndZervantSettings'
import { defaultValues, getValidationSchema, OrganizationInfoForm } from '../utils/formData'

interface ContextState {
  submitForm: () => void
  isProcessing: boolean
  isLoading: boolean
  isLegalStatusAndSharedCapitalVisible: boolean
}

const defaultValue: ContextState = {
  submitForm: noop,
  isProcessing: false,
  isLoading: false,
  isLegalStatusAndSharedCapitalVisible: false,
}

const OrganizationInfoSettingsContext = createContext<ContextState>(defaultValue)

interface OrganizationInfoSettingsContextProps {
  children: ReactNode
}

export const OrganizationInfoSettingsContextProvider = ({
  children,
}: OrganizationInfoSettingsContextProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { setDirty } = useDirtyRoute()

  const { organization, isLoading: isLoadingOrganization } = useUserOrganization()
  const { isUiSetupShineInvoicingAccounting, isUiSetupSupernavInvoicingAccounting } = useOrganizationUiSetup()

  const {
    Form,
    handleSubmit,
    reset: resetForm,
    formState: { isDirty },
  } = useForm({
    defaultValues,
    validationSchema: getValidationSchema(t, organization?.countryId),
  })

  const isZervantFrenchCompany =
    (isUiSetupShineInvoicingAccounting || isUiSetupSupernavInvoicingAccounting) && organization?.countryId === 'FR'

  const { data: zervantSettings, isLoading: isLoadingZervantSettings } = useZervantSettings({
    enabled: isZervantFrenchCompany,
  })

  const { update, isProcessing } = useUpdateOrganizationAndZervantSettings()
  const markAccountStepDone = useMarkAccountStepDone()

  const { saveWithWarning, modals: warningModals } = useVatConfirmationWarningModal()
  const { user, isLoading: isLoadingCurrentUser } = useCurrentUser()

  const isLoading = isLoadingCurrentUser || isLoadingOrganization || isLoadingZervantSettings

  const handleFormSubmit = useCallback(
    (values: OrganizationInfoForm) => {
      if (!organization) {
        return
      }
      const { isSiretPendingAssignment, legalStatusCustom, sharedCapital, legalStatusOption, ...payload } = values

      const legalStatusValue = legalStatusOption === FrenchLegalStatus.Other ? legalStatusCustom : legalStatusOption

      saveWithWarning({
        values,
        valuesToCompare: {
          isVatExempted: organization.isVatExempted,
        },
        onWarningsConfirm: async () => {
          const isSuccess = await update({
            payload,
            settingsPayload: { legalStatus: legalStatusValue, sharedCapital, isSiretPendingAssignment },
          })

          if (isSuccess) {
            markAccountStepDone(values)
            track('XXX - AG - Organization Settings - Organization Info Form Submitted')
          }
        },
      })
    },
    [markAccountStepDone, organization, saveWithWarning, track, update],
  )

  const handleValidationError = useCallback(() => {
    notify({
      id: NotificationKeys.OrganizationSettingsUpdate,
      message: t('settings.organization.organization_info.notification.validation_error'),
      variant: 'error',
    })
  }, [t])

  const submitForm = useCallback(() => {
    handleSubmit(handleFormSubmit, handleValidationError)()
  }, [handleFormSubmit, handleSubmit, handleValidationError])

  useEffect(() => {
    if (!organization?.id || isLoading || !user) {
      return
    }
    const settingsOrganizationValues = zervantSettings?.values.company

    const { legalStatusCustom, legalStatusOption } = parseLegalStatus(settingsOrganizationValues?.legalStatus)
    const isDefault = isCompanyNameDefault({
      companyName: organization.name,
      userEmail: user.email,
    })
    resetForm({
      addressLine2: organization?.addressLine2,
      city: organization?.city,
      countryId: organization?.countryId,
      email: organization?.email,
      fax: organization?.fax,
      isSiretPendingAssignment: zervantSettings?.values?.company?.legalFormSettings?.isSiretPendingAssignment ?? false,
      isVatExempted: organization?.isVatExempted,
      legalStatusCustom,
      legalStatusOption,
      name: isDefault ? '' : organization.name,
      phone: organization?.phone,
      registrationNo: organization?.registrationNo,
      sharedCapital: settingsOrganizationValues?.legalFormSettings?.sharedCapital,
      street: organization?.street,
      vatNo: organization?.vatNo,
      zipcode: organization?.zipcode,
    })
  }, [
    organization?.addressLine2,
    organization?.city,
    organization?.countryId,
    organization?.email,
    organization?.fax,
    organization?.id,
    organization?.isVatExempted,
    organization?.name,
    organization?.phone,
    organization?.registrationNo,
    organization?.street,
    organization?.vatNo,
    organization?.zipcode,
    isLoading,
    user,
    resetForm,
    zervantSettings?.values.company,
  ])

  useEffect(() => {
    setDirty(isDirty)
  }, [isDirty, setDirty])

  return (
    <OrganizationInfoSettingsContext.Provider
      value={{
        isLegalStatusAndSharedCapitalVisible: isZervantFrenchCompany,
        isLoading,
        isProcessing,
        submitForm,
      }}
    >
      <Form>
        {children}
        {warningModals}
      </Form>
    </OrganizationInfoSettingsContext.Provider>
  )
}

export const useOrganizationInfoSettings = () => useContext(OrganizationInfoSettingsContext)
